import { Dispatch, SetStateAction, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { ArchiveUsersResponse } from "@/model/Archive/ArchiveUsersResponse";
import {
	ArchiveProject,
	ArchivedProjectsRequest,
	ArchivedProjectsResponse,
} from "@/model/Archive/ArchivedProjectsRequest";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";

import { useFetchProcedureGroups } from "@/service/ProcedureQueries";
import { RouteParams, useLocalStorage } from "@/service/Utilities";

export const useFetchArchivedProcedures = (
	request: ArchivedProjectsRequest,
) => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);

	const queryKey = [
		"archive",
		request.StartDate,
		request.EndDate,
		request.SelectedUser,
	];

	if (id) {
		queryKey.push(procedureId);
		request = { ...request, ProcedureId: procedureId };
	}

	return useQuery<ArchivedProjectsResponse, any>({
		queryKey: queryKey,
		queryFn: () => SATOSPortal.QueryArchivedProcedures(request),
	});
};

export const useFetchArchivedProcedure = () => {
	const { id } = useParams<RouteParams>();
	const procedureId = parseInt(id);

	return useQuery<ArchiveProject, any>({
		queryKey: ["archive", "procedure", procedureId],
		queryFn: () => SATOSPortal.QueryArchivedProcedure(id),
	});
};

export const useFetchArchiveUsers = () => {
	return useQuery<ArchiveUsersResponse, any>({
		queryKey: ["archive", "users"],
		queryFn: () => SATOSPortal.QueryArchiveUsers(),
	});
};

// TODO: This should be a custom hook instead of a query - Ustin
export const useFetchArchiveProcedureGroupFolders = (): {
	groupList: ProcedureGroup[];
	setGroupList: Dispatch<SetStateAction<ProcedureGroup[]>>;
	closeAllProcedureGroupFolders: () => void;
	openAllProcedureGroupFolders: () => void;
} => {
	const [groupListLocalStorage, setGroupListLocalStorage] = useLocalStorage<
		ProcedureGroup[]
	>("archiveFoldersState", []);

	const { data: groups } = useFetchProcedureGroups("?$orderby=Name asc");

	useEffect(() => {
		if (!groupListLocalStorage || groupListLocalStorage.length === 0) {
			groups &&
				setGroupListLocalStorage(
					groups.sort((a, b) => sortArchiveGroupFolders(a, b)),
				);
		}

		if (groups && groupListLocalStorage) {
			if (groupListLocalStorage.length !== groups.length) {
				setGroupListLocalStorage(groups);
			} else {
				const sortedGroups = groups.sort((a, b) =>
					sortArchiveGroupFolders(a, b),
				);
				for (let i = 0; i < sortedGroups.length; i++) {
					if (
						sortedGroups[i].Id !== groupListLocalStorage[i].Id ||
						sortedGroups[i].Name !== groupListLocalStorage[i].Name
					) {
						setGroupListLocalStorage(sortedGroups);
						return;
					}
				}
			}
		}
	});

	const closeAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = false;
				return group;
			}),
		]);
	};

	const openAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = true;
				return group;
			}),
		]);
	};

	return {
		groupList: groupListLocalStorage,
		setGroupList: setGroupListLocalStorage,
		closeAllProcedureGroupFolders,
		openAllProcedureGroupFolders,
	};
};

export const sortArchiveGroupFolders = (
	a: ProcedureGroup,
	b: ProcedureGroup,
): number => {
	if (a.SortNumber === 0 && b.SortNumber === 0) {
		return a.Name.localeCompare(b.Name);
	}

	if (a.SortNumber === 0) {
		return 1;
	}

	if (b.SortNumber === 0) {
		return -1;
	}

	return a.SortNumber - b.SortNumber;
};
